import { type ChangeEvent, type MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';

import {
  useTrackAgentActionsMenuGroupOpened,
  useTrackAgentActionsMenuOpened,
  useTrackAgentActionsMenuSkillSelected,
  useTrackAgentActionsMenuSkillsOpened,
  useTrackAgentActionsMenuTaskSelected,
} from '@air/analytics';

import { usePrivateWorkspaceAgentActionsMenuOpen } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/PrivateWorkspaceAgentActionsMenuOpenProvider';
import { type WorkspaceAgentActionsMenuPanelSessionProps } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/workspaceAgentActionsMenuPanelSession';
import { useWorkspaceAgentGuidedVariantFlowStore } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/PrivateWorkspaceAgentGuidedVariantProvider';
import {
  WORKSPACE_AGENT_ACTIONS_MENU_SEARCH_PLACEHOLDER,
  WORKSPACE_AGENT_ACTIONS_MENU_SEARCH_PLACEHOLDER_WITH_SKILLS,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentActionsMenuLayout';
import {
  getWorkspaceAgentActionsMenuTaskGuidedVariantEditId,
  WORKSPACE_AGENT_ACTIONS_MENU_GROUPS,
  WORKSPACE_AGENT_ACTIONS_MENU_TASKS,
  type WorkspaceAgentActionsMenuGroup,
  type WorkspaceAgentActionsMenuGroupId,
  type WorkspaceAgentActionsMenuTask,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentActionsMenuTasks';
import { usePrivateWorkspaceAgentGuidedVariantFlowTracking } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentGuidedVariantFlowTracking';
import { usePrivateWorkspaceAgentSkillsCatalogPagination } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentSkillsCatalogPagination';
import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { usePrivateWorkspaceAgentSkillsCatalog } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentSkillsCatalogProvider';
import { usePrivateWorkspaceAgentActionsMenuSkillsSearch } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentActionsMenuSkillsSearch';
import { filterWorkspaceAgentActionsMenuTasks } from '~/components/v2/components/workspace/workspace-agent-panel/utils/filterWorkspaceAgentActionsMenuTasks';
import { useWorkspaceAgentComposerSeed } from '~/components/v2/components/workspace/workspace-agent-panel/WorkspaceAgentComposerSeedProvider';
import { type Skill } from '~/hooks/queries/skills/usePrivateSkillsList';

const ADD_REFERENCE_LABEL = 'Add reference';

export type UseWorkspaceAgentActionsMenuParams = {
  isDisabled: boolean;
  onAddReference: () => void;
  onInsertSkill: (skill: Skill) => void;
  waiveCloseAutoFocusRef: MutableRefObject<boolean>;
};

export type UseWorkspaceAgentActionsMenuResult = {
  fillsActionsMenuPopoverHeight: boolean;
  handleOpenChange: (isOpen: boolean) => void;
  isOpen: boolean;
  isSkillsSubview: boolean;
  panelProps: WorkspaceAgentActionsMenuPanelSessionProps;
  selectedGroup: WorkspaceAgentActionsMenuGroup | null;
};

export const useWorkspaceAgentActionsMenu = ({
  isDisabled,
  onAddReference,
  onInsertSkill,
  waiveCloseAutoFocusRef,
}: UseWorkspaceAgentActionsMenuParams): UseWorkspaceAgentActionsMenuResult => {
  const { seedComposer } = useWorkspaceAgentComposerSeed();
  const openGuidedVariantFlow = useWorkspaceAgentGuidedVariantFlowStore((state) => state.openGuidedVariantFlow);
  const { trackAgentActionsMenuOpened } = useTrackAgentActionsMenuOpened();
  const { trackAgentActionsMenuGroupOpened } = useTrackAgentActionsMenuGroupOpened();
  const { trackAgentActionsMenuTaskSelected } = useTrackAgentActionsMenuTaskSelected();
  const { trackAgentActionsMenuSkillsOpened } = useTrackAgentActionsMenuSkillsOpened();
  const { trackAgentActionsMenuSkillSelected } = useTrackAgentActionsMenuSkillSelected();
  const { sessionId } = usePrivateWorkspaceAgentPanelSession();
  const { trackGuidedVariantFlowOpened } = usePrivateWorkspaceAgentGuidedVariantFlowTracking({ chatId: sessionId });
  const { isSkillsListLoading, isWorkspaceAgentSlashSkillsEnabled, skills, workspaceId } =
    usePrivateWorkspaceAgentSkillsCatalog();
  const {
    hasSkillsListNextPage,
    isSkillsListFetchNextPageError,
    isSkillsListFetchingNextPage,
    requestSkillsListNextPage,
    retrySkillsListNextPage,
  } = usePrivateWorkspaceAgentSkillsCatalogPagination();
  const { actionsMenuEntryLocation, isOpen: isStoreOpen, setIsOpen } = usePrivateWorkspaceAgentActionsMenuOpen();
  // Zero-state opens via `openActionsMenu(location)`; composer button uses Radix `handleOpenChange`. Run lock gates `isOpen`.
  const isOpen = isStoreOpen && !isDisabled;
  const [query, setQuery] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState<WorkspaceAgentActionsMenuGroupId | null>(null);
  const [isSkillsSubview, setIsSkillsSubview] = useState(false);
  const trimmedQuery = query.trim();
  const normalizedQuery = trimmedQuery.toLowerCase();
  const isSearching = normalizedQuery.length > 0;
  const blendedSkillsSearchEnabled = isOpen && isWorkspaceAgentSlashSkillsEnabled && isSearching;

  const blendedSkillsSearch = usePrivateWorkspaceAgentActionsMenuSkillsSearch({
    enabled: blendedSkillsSearchEnabled,
    query: trimmedQuery,
    workspaceId,
  });

  const visibleTasks = useMemo((): readonly WorkspaceAgentActionsMenuTask[] => {
    if (!isSearching && !selectedGroupId) {
      return [];
    }

    if (isSkillsSubview && !isSearching) {
      return [];
    }

    const groupTasks = selectedGroupId
      ? WORKSPACE_AGENT_ACTIONS_MENU_TASKS.filter((task) => task.groupId === selectedGroupId)
      : WORKSPACE_AGENT_ACTIONS_MENU_TASKS;

    return filterWorkspaceAgentActionsMenuTasks(groupTasks, query);
  }, [isSearching, isSkillsSubview, query, selectedGroupId]);

  const visibleGroups = isSearching || selectedGroupId || isSkillsSubview ? [] : WORKSPACE_AGENT_ACTIONS_MENU_GROUPS;

  const visibleBlendedSearchSkills: readonly Skill[] = blendedSkillsSearchEnabled ? blendedSkillsSearch.skills : [];

  const visibleSkills = useMemo((): readonly Skill[] => {
    if (!isSkillsSubview || isSearching) {
      return [];
    }

    return skills;
  }, [isSearching, isSkillsSubview, skills]);

  const selectedGroup = WORKSPACE_AGENT_ACTIONS_MENU_GROUPS.find((group) => group.id === selectedGroupId) ?? null;

  const isAddReferenceVisible =
    !selectedGroup && !isSkillsSubview && ADD_REFERENCE_LABEL.toLowerCase().includes(normalizedQuery);

  const isSavedSkillsEntryVisible =
    isWorkspaceAgentSlashSkillsEnabled && !selectedGroup && !isSkillsSubview && !isSearching;

  const isBlendedSkillsSearchLoading = blendedSkillsSearchEnabled && blendedSkillsSearch.isLoading;

  const isSkillsCatalogSubview = isSkillsSubview && !isSearching;

  const showBlendedSearchEmpty =
    isSearching &&
    visibleTasks.length === 0 &&
    visibleBlendedSearchSkills.length === 0 &&
    !isBlendedSkillsSearchLoading &&
    !isAddReferenceVisible &&
    !isSavedSkillsEntryVisible;

  const showEmpty =
    showBlendedSearchEmpty || (!isSearching && !!selectedGroupId && !isSkillsSubview && visibleTasks.length === 0);

  const showSkillsEmpty = isSkillsCatalogSubview && !isSkillsListLoading && visibleSkills.length === 0;

  const searchPlaceholder = isWorkspaceAgentSlashSkillsEnabled
    ? WORKSPACE_AGENT_ACTIONS_MENU_SEARCH_PLACEHOLDER_WITH_SKILLS
    : WORKSPACE_AGENT_ACTIONS_MENU_SEARCH_PLACEHOLDER;

  // Skills-on menus always use fixed popover height. Skills-off stays shrink-to-content on the home
  // group list, but search and group drill-in keep height stable so filtering rows does not collapse the panel.
  const fillsActionsMenuPopoverHeight = isWorkspaceAgentSlashSkillsEnabled || isSearching || selectedGroupId !== null;

  const resetToHome = useCallback(() => {
    setQuery('');
    setSelectedGroupId(null);
    setIsSkillsSubview(false);
  }, []);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    resetToHome();
  }, [resetToHome, setIsOpen]);

  useEffect(() => {
    if (!isDisabled || !isStoreOpen) {
      return;
    }

    setIsOpen(false);
    resetToHome();
  }, [isDisabled, isStoreOpen, resetToHome, setIsOpen]);

  const handleOpenChange = useCallback(
    (isNextOpen: boolean) => {
      if (isNextOpen && isDisabled) {
        return;
      }

      if (isNextOpen && !isOpen) {
        trackAgentActionsMenuOpened({ location: 'composer_button' });
      }

      setIsOpen(isNextOpen);

      if (!isNextOpen) {
        resetToHome();
      }
    },
    [isDisabled, isOpen, resetToHome, setIsOpen, trackAgentActionsMenuOpened],
  );

  const handleQueryChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  }, []);

  const handleSelectGroup = useCallback(
    (groupId: WorkspaceAgentActionsMenuGroupId) => {
      trackAgentActionsMenuGroupOpened({ group_id: groupId });
      setSelectedGroupId(groupId);
    },
    [trackAgentActionsMenuGroupOpened],
  );

  const handleBack = useCallback(() => {
    setSelectedGroupId(null);
  }, []);

  const handleOpenSavedSkills = useCallback(() => {
    trackAgentActionsMenuSkillsOpened({ location: actionsMenuEntryLocation });
    setIsSkillsSubview(true);
  }, [actionsMenuEntryLocation, trackAgentActionsMenuSkillsOpened]);

  const handleBackFromSkills = useCallback(() => {
    setIsSkillsSubview(false);
  }, []);

  const handleSelectTask = useCallback(
    (task: WorkspaceAgentActionsMenuTask) => {
      trackAgentActionsMenuTaskSelected({ group_id: task.groupId, task_id: task.id, via_search: isSearching });
      // Seed focuses the editor. Guided edits lock the composer — don't restore focus to Actions.
      waiveCloseAutoFocusRef.current = true;
      closeMenu();

      const guidedVariantEditId = getWorkspaceAgentActionsMenuTaskGuidedVariantEditId(task.id);

      if (guidedVariantEditId) {
        trackGuidedVariantFlowOpened({ entry: 'actions_menu' });
        openGuidedVariantFlow(guidedVariantEditId);
        return;
      }

      seedComposer(task.prompt, { actionsMenuTaskId: task.id, sendImmediately: false, source: 'actions_menu' });
    },
    [
      closeMenu,
      isSearching,
      openGuidedVariantFlow,
      seedComposer,
      trackAgentActionsMenuTaskSelected,
      trackGuidedVariantFlowOpened,
      waiveCloseAutoFocusRef,
    ],
  );

  const handleSelectSkill = useCallback(
    (skill: Skill) => {
      trackAgentActionsMenuSkillSelected({ skill_id: skill.id, via_search: isSearching });
      waiveCloseAutoFocusRef.current = true;
      onInsertSkill(skill);
      closeMenu();
    },
    [closeMenu, isSearching, onInsertSkill, trackAgentActionsMenuSkillSelected, waiveCloseAutoFocusRef],
  );

  const handleAddReference = useCallback(() => {
    waiveCloseAutoFocusRef.current = true;
    closeMenu();
    onAddReference();
  }, [closeMenu, onAddReference, waiveCloseAutoFocusRef]);

  const panelProps = useMemo(
    (): WorkspaceAgentActionsMenuPanelSessionProps => ({
      addReferenceLabel: ADD_REFERENCE_LABEL,
      fillsActionsMenuPopoverHeight,
      handleAddReference,
      handleBack,
      handleBackFromSkills,
      handleOpenSavedSkills,
      handleQueryChange,
      handleSelectGroup,
      handleSelectSkill,
      handleSelectTask,
      isAddReferenceVisible,
      isBlendedSkillsSearchLoading,
      isSavedSkillsEntryVisible,
      hasSkillsListNextPage,
      isSkillsListFetchNextPageError,
      isSkillsListFetchingNextPage,
      isSkillsListLoading: isSkillsCatalogSubview && isSkillsListLoading,
      isSearching,
      isSkillsSubview,
      query,
      requestSkillsListNextPage,
      retrySkillsListNextPage,
      searchPlaceholder,
      selectedGroup,
      showEmpty,
      showSkillsEmpty,
      visibleBlendedSearchSkills,
      visibleGroups,
      visibleSkills,
      visibleTasks,
    }),
    [
      fillsActionsMenuPopoverHeight,
      handleAddReference,
      handleBack,
      handleBackFromSkills,
      handleOpenSavedSkills,
      handleQueryChange,
      handleSelectGroup,
      handleSelectSkill,
      handleSelectTask,
      isAddReferenceVisible,
      isBlendedSkillsSearchLoading,
      isSavedSkillsEntryVisible,
      hasSkillsListNextPage,
      isSkillsCatalogSubview,
      isSkillsListFetchNextPageError,
      isSkillsListFetchingNextPage,
      isSkillsListLoading,
      isSearching,
      isSkillsSubview,
      query,
      requestSkillsListNextPage,
      retrySkillsListNextPage,
      searchPlaceholder,
      selectedGroup,
      showEmpty,
      showSkillsEmpty,
      visibleBlendedSearchSkills,
      visibleGroups,
      visibleSkills,
      visibleTasks,
    ],
  );

  return useMemo(
    (): UseWorkspaceAgentActionsMenuResult => ({
      fillsActionsMenuPopoverHeight,
      handleOpenChange,
      isOpen,
      isSkillsSubview,
      panelProps,
      selectedGroup,
    }),
    [fillsActionsMenuPopoverHeight, handleOpenChange, isOpen, isSkillsSubview, panelProps, selectedGroup],
  );
};
